import React from "react";
import styled from "styled-components";
import { Row, Col, Card, Form, Button, Stack, Spinner } from "react-bootstrap";
import Table from "./Table";
import { useState, useMemo } from "react";
import Pagination from "../../organisms/Pagination";
import TableTitle from "../../organisms/TableTitle";
import {
  ItemListadoAsistenciaDiaria,
  QUERY_OBTENER_ASISTENCIA_DIARIA,
  useObtenerListaAsistencia,
} from "../../../services/asistenciaServicios";
import { checkIn } from "../../../services/asistenciaServicios";
import { formatearFecha } from "../../../utils/dateUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import SuccessButton from "../../organisms/SuccessButton";
import CheckIcon from "@mui/icons-material/Check";
import LoadingScreen from "../../organisms/LoadingScreen";
import { filterByStringIgnoreCase } from "../../../utils/tableUtils";

function TableSection({ idLista }: { idLista: string }) {
  const queryClient = useQueryClient();

  const {
    data: listaAsistencia,
    isLoading: listaAsistenciaIsLoading,
    isSuccess: listaAsistenciaIsSuccess,
    error: listaAsistenciaError,
  } = useObtenerListaAsistencia(idLista);

  const checkeo = useMutation(checkIn, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_OBTENER_ASISTENCIA_DIARIA],
      });
    },
  });

  const [filtro, setFiltro] = useState("");
  const [finalFilter, setFinalFilter] = useState("");
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [itemsToShow, setItemsToShow] = useState(20);
  const [itemsACheckear, setItemsACheckear] = useState(
    new Map<string, boolean>()
  );
  const items = useMemo(() => {
    let asistencias = listaAsistencia?.asistencias || [];
    asistencias.sort(
      (
        itemA: ItemListadoAsistenciaDiaria,
        itemB: ItemListadoAsistenciaDiaria
      ) => {
        return itemA.nombreMascota.localeCompare(itemB.nombreMascota);
      }
    );
    return asistencias;
  }, [listaAsistencia]);

  const onBuscar = (newFilter: string) => setFinalFilter(newFilter);

  const itemsFiltrados = useMemo(
    () => filterByStringIgnoreCase(items, finalFilter),
    [items, finalFilter]
  );

  const page = useMemo(
    () => generatePage(itemsFiltrados, activePageNumber, itemsToShow),
    [itemsFiltrados, activePageNumber, itemsToShow]
  );

  const error = listaAsistenciaError || checkeo.error;

  const onCheckIn = () => {
    checkeo.mutate({
      idListaAsistencia: listaAsistencia!!.id,
      asistencias: itemsACheckear,
    });
  };

  const onAsistioChange = (payload: { id: string; asistio: boolean }) => {
    setItemsACheckear((prevItemsACheckear) => {
      let newItemsACheckear = prevItemsACheckear;

      newItemsACheckear.set(payload.id, payload.asistio);

      return newItemsACheckear;
    });
  };

  if (error) {
    return <ServerErrorModal />;
  }

  if (listaAsistenciaIsLoading) {
    return <LoadingScreen isLoading={true} />;
  }

  return (
    <Row>
      <Col className="col-12">
        <StyledCard>
          <StyledCardHeader>
            <TableTitle>
              {"Lista de asistencia para " +
                formatearFecha(new Date(listaAsistencia!!.fechaGeneracion))}
            </TableTitle>
          </StyledCardHeader>
          <StyledCardBody>
            <ResponsiveTableSection>
              <TableWrapper>
                <Stack gap={4}>
                  <TableLengthSection>
                    <Form>
                      <StyledFormGroup as={Row}>
                        <Form.Label column sm="auto">
                          Items:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormSelect
                            value={itemsToShow}
                            onChange={(
                              e: React.ChangeEvent<HTMLSelectElement>
                            ) => setItemsToShow(parseInt(e.target.value))}
                          >
                            {[5, 10, 20].map((pageSize, index) => (
                              <option key={index} value={pageSize}>
                                {pageSize}
                              </option>
                            ))}
                          </StyledFormSelect>
                        </Col>
                        <Form.Label column sm="auto">
                          Filtro:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormControl
                            type="text"
                            value={filtro}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setFiltro(e.target.value)}
                          />
                        </Col>
                        {listaAsistencia && listaAsistenciaIsSuccess && (
                          <>
                            <Col sm="auto">
                              <Button
                                variant="primary"
                                onClick={() => onBuscar(filtro)}
                              >
                                Buscar
                              </Button>
                            </Col>
                            <Col sm="auto">
                              <SuccessButton
                                variant="success"
                                onClick={onCheckIn}
                              >
                                Guardar{" "}
                                {checkeo.isLoading && (
                                  <Spinner
                                    animation="border"
                                    variant="light"
                                    size="sm"
                                  />
                                )}
                                {!checkeo.isLoading && (
                                  <CheckIcon sx={{ fontSize: 20 }} />
                                )}
                              </SuccessButton>
                            </Col>
                          </>
                        )}
                      </StyledFormGroup>
                    </Form>
                  </TableLengthSection>
                  <Table
                    items={page}
                    isLoading={listaAsistenciaIsLoading}
                    isSuccess={listaAsistenciaIsSuccess}
                    onAsistioChange={onAsistioChange}
                  />
                  <div>
                    <Pagination
                      pageSize={itemsToShow}
                      setActivePageNumber={setActivePageNumber}
                      activePageNumber={activePageNumber}
                      rows={itemsFiltrados}
                    />
                  </div>
                </Stack>
              </TableWrapper>
            </ResponsiveTableSection>
          </StyledCardBody>
        </StyledCard>
      </Col>
    </Row>
  );
}

function generatePage<T>(
  rows: Array<T>,
  pageNumber: number,
  pageSize: number
): Array<T> {
  const end = pageNumber * pageSize;
  const start = end - pageSize;
  return rows.slice(start, end);
}

const StyledCard = styled(Card)`
  margin-bottom: 1.875rem;
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  padding: 1.25rem 1.25rem 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 0px;
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;
const ResponsiveTableSection = styled("div")`
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TableWrapper = styled("div")`
  position: relative;
  clear: both;
  zoom: 1;
`;

const TableLengthSection = styled("div")`
  float: left;
`;

const StyledFormGroup = styled(Form.Group)`
  justify-content: start;
`;
const StyledFormControl = styled(Form.Control)`
  &:focus {
    box-shadow: none;
  }
`;

const StyledFormSelect = styled(Form.Select)`
  &:focus {
    box-shadow: none;
  }
`;

export default TableSection;
